import {
    Divider,
    Grid,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Menu,
    MenuItem,
    MenuItemProps,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    PopoverOrigin,
    PopoverPosition,
    PopoverReference,
} from "@mui/material/Popover/Popover";
import { styled } from "@mui/material";
import { observer } from "mobx-react";
import React, { FunctionComponent, forwardRef } from "react";
import theme from "../../../Theme/AppTheme";
import { isStringType } from "../../../utils/TypeGuards";

const defaultAnchorOrigin: PopoverOrigin = {
    vertical: "bottom",
    horizontal: "right",
};

const defaultTransformOrigin: PopoverOrigin = {
    vertical: "top",
    horizontal: "right",
};

export const StyledMenuLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    "&:hover, &.Mui-selected:hover": {
        color: theme.palette.neutral[600],
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
            color: theme.palette.neutral[600],
        },
    },
    "&:focus, &:active, &.Mui-selected": {
        color: theme.palette.primary[500],
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
            color: theme.palette.primary[500],
        },
    },
}));

const StyledListItemContainer = styled(Grid)(({ theme }) => ({
    display: "inline-flex",
    color: theme.palette.text.primary,

    "&:hover, &.Mui-selected:hover": {
        color: theme.palette.neutral[600],
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
            color: theme.palette.neutral[600],
        },
    },

    "&:focus, &:active, &.Mui-selected": {
        color: theme.palette.primary[500],
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
            color: theme.palette.primary[500],
        },
    },
}));

export type AcxMenuItemProps = {
    id: string;
    label: React.ReactNode;
    secondaryLabel?: string;
    icon?: React.ReactNode;
    props?: Omit<MenuItemProps, "button">;
    action?: React.ReactNode;
    hidden?: boolean;
    toolTipText?: string;
    lastItemOfSection?: boolean;
};
interface IOwnProps {
    anchorElement?: HTMLElement | null;
    menuItems: Array<AcxMenuItemProps | "Divider">;
    onMenuClose: (evt) => void;
    open?: boolean;
    disableAutoFocusItem?: boolean;
    anchorPosition?: PopoverPosition;
    anchorReference?: PopoverReference;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    rootStyle?: React.CSSProperties;
    sx?: SxProps<Theme>;
}

const AcxMenu: FunctionComponent<IOwnProps> = observer((props) => {
    const menuIcon = (value: AcxMenuItemProps) => {
        if (value.icon) {
            return (
                <StyledListItemContainer style={props.rootStyle}>
                    <ListItemIcon>{value.icon}</ListItemIcon>

                    <ListItemText
                        disableTypography={!isStringType(value.label)}
                        style={{
                            marginRight: Boolean(value.action) ? "24px" : 0,
                        }}
                        primary={value.label}
                        secondary={value.secondaryLabel}
                    />
                </StyledListItemContainer>
            );
        } else {
            return (
                <StyledListItemContainer>
                    <ListItemText
                        disableTypography={!isStringType(value.label)}
                        style={{
                            marginRight: Boolean(value.action) ? "24px" : 0,
                        }}
                        primary={value.label}
                        secondary={value.secondaryLabel}
                    />
                </StyledListItemContainer>
            );
        }
    };

    const isOpen = props.open || Boolean(props.anchorElement);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            {props.menuItems.length > 0 && (
                <Menu
                    anchorOrigin={props.anchorOrigin ?? defaultAnchorOrigin}
                    transformOrigin={
                        props.transformOrigin ?? defaultTransformOrigin
                    }
                    anchorEl={props.anchorElement}
                    anchorPosition={props.anchorPosition}
                    anchorReference={props.anchorReference ?? "anchorEl"}
                    disableAutoFocusItem={props.disableAutoFocusItem}
                    keepMounted
                    open={isOpen}
                    onClose={props.onMenuClose}
                    sx={[
                        {
                            "& .MuiPaper-root": {
                                border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                                borderRadius: 2,
                            },
                            marginTop: theme.spacing(1),
                        },
                        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
                    ]}
                >
                    {props.menuItems
                        .filter((value) => {
                            if (value === "Divider") {
                                return true;
                            } else {
                                return !value.hidden;
                            }
                        })
                        .map((value, i) => {
                            return value === "Divider" ? (
                                <Divider
                                    key={`acx-menu-item-key-divider-` + i}
                                />
                            ) : value.toolTipText ? (
                                <Tooltip
                                    key={value.id}
                                    title={value.toolTipText}
                                    placement="right"
                                >
                                    <span style={{ display: "block" }}>
                                        <AcxMenuItem
                                            {...value.props}
                                            lastItemOfSection={
                                                value.lastItemOfSection
                                            }
                                            id={value.id}
                                        >
                                            {menuIcon(value)}
                                            {Boolean(value.action) && (
                                                <ListItemSecondaryAction>
                                                    {value.action}
                                                </ListItemSecondaryAction>
                                            )}
                                        </AcxMenuItem>
                                    </span>
                                </Tooltip>
                            ) : (
                                <AcxMenuItem
                                    key={value.id}
                                    {...value.props}
                                    lastItemOfSection={value.lastItemOfSection}
                                    id={value.id}
                                >
                                    {menuIcon(value)}
                                    {Boolean(value.action) && (
                                        <ListItemSecondaryAction>
                                            {value.action}
                                        </ListItemSecondaryAction>
                                    )}
                                </AcxMenuItem>
                            );
                        })}
                </Menu>
            )}
        </>
    );
});

export function AcxMenuItem({
    lastItemOfSection,
    ...props
}: React.ComponentPropsWithoutRef<typeof MenuItem> & {
    lastItemOfSection?: boolean;
    icon?: React.ReactNode;
}) {
    return (
        <MenuItem
            divider={lastItemOfSection}
            {...props}
            disableGutters={false}
            dense={true}
            id={props.id}
            sx={[
                {
                    ...(lastItemOfSection && {
                        margin: 0,
                    }),
                    paddingLeft: "16px",
                    minWidth: "190px",
                    minHeight: "37px",
                    borderRadius: "2px",
                    color: theme.palette.text.primary,
                    "&:hover": {
                        backgroundColor: theme.palette.primary[50],
                        color: theme.palette.neutral[600],
                        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                            color: theme.palette.neutral[600],
                        },
                    },
                    "&:focus": {
                        backgroundColor: theme.palette.primary[50],
                        color: theme.palette.neutral[600],
                        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                            color: theme.palette.neutral[600],
                        },
                    },
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            {props.icon && (
                <Grid
                    sx={{
                        marginRight: 1.5,
                        display: "flex",
                        justifyItems: "center",
                        alignContent: "center",
                    }}
                >
                    {props.icon}
                </Grid>
            )}
            {props.children}
        </MenuItem>
    );
}

export const AcxMenuV2 = forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithRef<typeof Menu>
>(function AcxMenuV2(props, ref) {
    return (
        <Menu
            ref={ref}
            anchorOrigin={defaultAnchorOrigin}
            transformOrigin={defaultTransformOrigin}
            anchorReference="anchorEl"
            {...props}
            sx={[
                {
                    "& .MuiPaper-root": {
                        border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                        borderRadius: 2,
                    },
                    "& .MuiList-root": {
                        tabindex: -1,
                    },
                    marginTop: theme.spacing(1),
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            {props.children}
        </Menu>
    );
});

export default AcxMenu;
